/**
 * 书签上下文
 * 管理书签的全局状态和操作
 */

import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { message } from 'antd';
// import BookmarkService from '../services/bookmarkService';
import MockBookmarkService from '../services/mockBookmarkService';
import { BOOKMARK_CATEGORIES, CATEGORY_LABELS } from '../models/Bookmark';
import defaultBookmarks from '../data/defaultBookmarks';
import { useAuth } from './AuthContext';

// 使用MockBookmarkService代替真实的MongoDB服务
const BookmarkService = MockBookmarkService;

// 创建书签上下文
const BookmarkContext = createContext();

// 书签状态类型
const BOOKMARK_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_BOOKMARKS: 'SET_BOOKMARKS',
  ADD_BOOKMARK: 'ADD_BOOKMARK',
  UPDATE_BOOKMARK: 'UPDATE_BOOKMARK',
  DELETE_BOOKMARK: 'DELETE_BOOKMARK',
  SET_SEARCH_TEXT: 'SET_SEARCH_TEXT',
  SET_CURRENT_CATEGORY: 'SET_CURRENT_CATEGORY',
  SET_STATS: 'SET_STATS',
  SET_ERROR: 'SET_ERROR'
};

// 初始状态
const initialState = {
  bookmarks: [],
  filteredBookmarks: [],
  isLoading: false,
  searchText: '',
  currentCategory: 'all',
  stats: {
    total: 0,
    totalClicks: 0,
    avgClicksPerBookmark: 0,
    categoriesCount: 0,
    categoryStats: {}
  },
  error: null
};

// 状态管理器
const bookmarkReducer = (state, action) => {
  switch (action.type) {
    case BOOKMARK_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };

    case BOOKMARK_ACTIONS.SET_BOOKMARKS:
      const bookmarks = action.payload;
      return {
        ...state,
        bookmarks,
        filteredBookmarks: filterBookmarks(bookmarks, state.searchText, state.currentCategory)
      };

    case BOOKMARK_ACTIONS.ADD_BOOKMARK:
      const newBookmarks = [...state.bookmarks, action.payload];
      return {
        ...state,
        bookmarks: newBookmarks,
        filteredBookmarks: filterBookmarks(newBookmarks, state.searchText, state.currentCategory)
      };

    case BOOKMARK_ACTIONS.UPDATE_BOOKMARK:
      const updatedBookmarks = state.bookmarks.map(bookmark =>
        bookmark._id === action.payload._id ? action.payload : bookmark
      );
      return {
        ...state,
        bookmarks: updatedBookmarks,
        filteredBookmarks: filterBookmarks(updatedBookmarks, state.searchText, state.currentCategory)
      };

    case BOOKMARK_ACTIONS.DELETE_BOOKMARK:
      const remainingBookmarks = state.bookmarks.filter(
        bookmark => bookmark._id !== action.payload
      );
      return {
        ...state,
        bookmarks: remainingBookmarks,
        filteredBookmarks: filterBookmarks(remainingBookmarks, state.searchText, state.currentCategory)
      };

    case BOOKMARK_ACTIONS.SET_SEARCH_TEXT:
      const searchText = action.payload;
      return {
        ...state,
        searchText,
        filteredBookmarks: filterBookmarks(state.bookmarks, searchText, state.currentCategory)
      };

    case BOOKMARK_ACTIONS.SET_CURRENT_CATEGORY:
      const category = action.payload;
      return {
        ...state,
        currentCategory: category,
        filteredBookmarks: filterBookmarks(state.bookmarks, state.searchText, category)
      };

    case BOOKMARK_ACTIONS.SET_STATS:
      return {
        ...state,
        stats: action.payload
      };

    case BOOKMARK_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload
      };

    default:
      return state;
  }
};

// 过滤书签的辅助函数
const filterBookmarks = (bookmarks, searchText, category) => {
  let filtered = bookmarks;

  // 按分类过滤
  if (category && category !== 'all') {
    filtered = filtered.filter(bookmark => bookmark.category === category);
  }

  // 按搜索文本过滤
  if (searchText) {
    const lowerSearchText = searchText.toLowerCase();
    filtered = filtered.filter(bookmark =>
      bookmark.title.toLowerCase().includes(lowerSearchText) ||
      bookmark.description.toLowerCase().includes(lowerSearchText) ||
      bookmark.url.toLowerCase().includes(lowerSearchText) ||
      (bookmark.tags && bookmark.tags.some(tag => 
        tag.toLowerCase().includes(lowerSearchText)
      ))
    );
  }

  return filtered;
};

// BookmarkProvider 组件
export const BookmarkProvider = ({ children }) => {
  const [state, dispatch] = useReducer(bookmarkReducer, initialState);
  const { user } = useAuth();

  // 加载用户书签
  const loadBookmarks = async () => {
    if (!user) return;

    try {
      dispatch({ type: BOOKMARK_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: BOOKMARK_ACTIONS.SET_ERROR, payload: null });

      const bookmarks = await BookmarkService.getUserBookmarks(user.id);
      
      // 如果用户没有书签，导入默认书签
      if (bookmarks.length === 0) {
        await importDefaultBookmarks();
        return;
      }

      dispatch({ type: BOOKMARK_ACTIONS.SET_BOOKMARKS, payload: bookmarks });
    } catch (error) {
      console.error('加载书签失败:', error);
      dispatch({ type: BOOKMARK_ACTIONS.SET_ERROR, payload: error.message });
      message.error('加载书签失败');
    } finally {
      dispatch({ type: BOOKMARK_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // 导入默认书签
  const importDefaultBookmarks = async () => {
    if (!user) return;

    try {
      const importedCount = await BookmarkService.importDefaultBookmarks(defaultBookmarks, user.id);
      message.success(`成功导入 ${importedCount} 个默认书签`);
      
      // 重新加载书签
      const bookmarks = await BookmarkService.getUserBookmarks(user.id);
      dispatch({ type: BOOKMARK_ACTIONS.SET_BOOKMARKS, payload: bookmarks });
    } catch (error) {
      console.error('导入默认书签失败:', error);
      message.error('导入默认书签失败');
    }
  };

  // 创建书签
  const createBookmark = async (bookmarkData) => {
    if (!user) return;

    try {
      dispatch({ type: BOOKMARK_ACTIONS.SET_LOADING, payload: true });
      
      const newBookmark = await BookmarkService.createBookmark(bookmarkData, user.id);
      dispatch({ type: BOOKMARK_ACTIONS.ADD_BOOKMARK, payload: newBookmark });
      
      message.success('书签创建成功');
      return newBookmark;
    } catch (error) {
      console.error('创建书签失败:', error);
      message.error(error.message || '创建书签失败');
      throw error;
    } finally {
      dispatch({ type: BOOKMARK_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // 更新书签
  const updateBookmark = async (bookmarkId, updateData) => {
    if (!user) return;

    try {
      dispatch({ type: BOOKMARK_ACTIONS.SET_LOADING, payload: true });
      
      const updatedBookmark = await BookmarkService.updateBookmark(bookmarkId, updateData, user.id);
      dispatch({ type: BOOKMARK_ACTIONS.UPDATE_BOOKMARK, payload: updatedBookmark });
      
      message.success('书签更新成功');
      return updatedBookmark;
    } catch (error) {
      console.error('更新书签失败:', error);
      message.error(error.message || '更新书签失败');
      throw error;
    } finally {
      dispatch({ type: BOOKMARK_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // 删除书签
  const deleteBookmark = async (bookmarkId) => {
    if (!user) return;

    try {
      dispatch({ type: BOOKMARK_ACTIONS.SET_LOADING, payload: true });
      
      const success = await BookmarkService.deleteBookmark(bookmarkId, user.id);
      if (success) {
        dispatch({ type: BOOKMARK_ACTIONS.DELETE_BOOKMARK, payload: bookmarkId });
        message.success('书签删除成功');
      } else {
        throw new Error('删除失败');
      }
    } catch (error) {
      console.error('删除书签失败:', error);
      message.error(error.message || '删除书签失败');
      throw error;
    } finally {
      dispatch({ type: BOOKMARK_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // 搜索书签
  const searchBookmarks = async (searchText) => {
    if (!user) return;

    try {
      dispatch({ type: BOOKMARK_ACTIONS.SET_SEARCH_TEXT, payload: searchText });
      
      if (searchText.trim()) {
        const searchResults = await BookmarkService.searchBookmarks(user.id, searchText);
        dispatch({ type: BOOKMARK_ACTIONS.SET_BOOKMARKS, payload: searchResults });
      } else {
        // 如果搜索文本为空，重新加载所有书签
        await loadBookmarks();
      }
    } catch (error) {
      console.error('搜索书签失败:', error);
      message.error('搜索失败');
    }
  };

  // 记录书签点击
  const recordBookmarkClick = async (bookmarkId) => {
    if (!user) return;

    try {
      const updatedBookmark = await BookmarkService.recordBookmarkClick(bookmarkId, user.id);
      dispatch({ type: BOOKMARK_ACTIONS.UPDATE_BOOKMARK, payload: updatedBookmark });
    } catch (error) {
      console.error('记录点击失败:', error);
      // 静默失败，不显示错误消息
    }
  };

  // 设置当前分类
  const setCurrentCategory = (category) => {
    dispatch({ type: BOOKMARK_ACTIONS.SET_CURRENT_CATEGORY, payload: category });
  };

  // 加载统计信息
  const loadStats = async () => {
    if (!user) return;

    try {
      const stats = await BookmarkService.getBookmarkStats(user.id);
      dispatch({ type: BOOKMARK_ACTIONS.SET_STATS, payload: stats });
    } catch (error) {
      console.error('加载统计信息失败:', error);
    }
  };

  // 当用户登录状态改变时，加载书签
  useEffect(() => {
    if (user) {
      loadBookmarks();
      loadStats();
    } else {
      // 用户登出时清空状态
      dispatch({ type: BOOKMARK_ACTIONS.SET_BOOKMARKS, payload: [] });
      dispatch({ type: BOOKMARK_ACTIONS.SET_STATS, payload: initialState.stats });
    }
  }, [user]);

  // 上下文值
  const contextValue = {
    // 状态
    bookmarks: state.bookmarks,
    filteredBookmarks: state.filteredBookmarks,
    isLoading: state.isLoading,
    searchText: state.searchText,
    currentCategory: state.currentCategory,
    stats: state.stats,
    error: state.error,

    // 操作
    loadBookmarks,
    createBookmark,
    updateBookmark,
    deleteBookmark,
    searchBookmarks,
    recordBookmarkClick,
    setCurrentCategory,
    loadStats,
    importDefaultBookmarks,

    // 常量
    categories: BOOKMARK_CATEGORIES,
    categoryLabels: CATEGORY_LABELS
  };

  return (
    <BookmarkContext.Provider value={contextValue}>
      {children}
    </BookmarkContext.Provider>
  );
};

// 自定义Hook
export const useBookmarks = () => {
  const context = useContext(BookmarkContext);
  if (!context) {
    throw new Error('useBookmarks必须在BookmarkProvider内使用');
  }
  return context;
};

export default BookmarkContext; 